<template>
  <div class="front-wrapper">
    <Header :regimen="regimen" />
    
    <!-- Steps Section -->
    <div class="container-fluid bg-white step_section">
      <div class="steps-wrap">
        <div class="step current">
          <p>Enter Regimen Information</p>
          <span class="step-marker" />
        </div>
        <div class="step">
          <p>Create Preset Regimen</p>
          <span class="step-marker" />
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="container container-md-lg section_wraper mt-4 pt-3">
      <ValidationObserver
        ref="observer"
        v-slot="{ invalid }"
        tag="form"
        @submit.prevent="enterInfo"
      >
        <div class="row row-layout-370 justify-content-center">
          <div class="col-md-12">
            <div class="d-box with-lpad pb-4 bg_box">
              <div class="form_title mb-4">
                <h3 class="mb-sm-4 mb-1">
                  Enter Regimen Information
                </h3>
              </div>

              <!-- Regimen Type Toggle -->
              <div class="row form form-gray pb-3">
                <div class="col-xl-12">
                  <div class="form-group regimen-type">
                    <label class="form-check-label">
                      Regimen Type: <span class="required">*</span>
                    </label>
                    <div class="regimen-logic">
                      <span class="toggle-text" :class="{active: isRegimenLogic}">Regimen Logic</span>
                      <label class="toggle-wrapper">
                        <input type="checkbox" :checked="!isRegimenLogic" @change="handleToggleChange" />
                        <span class="toggle-switch" />
                      </label>
                      <span class="toggle-text" :class="{active: !isRegimenLogic}">Custom Regimen</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Concerns -->
              <div class="row form form-gray pb-3">
                <div class="col-xl-12 form-group mb-2">
                  <label class="form-check-label">
                    Concern<span v-if="!isRegimenLogic">s</span> <u v-if="!isRegimenLogic">(can select multiple)</u><span class="required">*</span>
                  </label>
                </div>
                <div class="col-xl-12">
                  <div class="row">
                    <div
                      v-for="tag in filteredTags"
                      :key="tag.name"
                      class="col-xl-4 col-sm-6"
                    >
                      <!-- Real checkbox only for selectable tags -->
                      <input
                        v-if="!tag.disabled"
                        :id="tag.name"
                        type="checkbox"
                        :checked="tagChacked(tag.name)"
                        @change="handleTag(tag.name)"
                      >

                      <!-- Visual-only checkbox for already-used tags -->
                      <span
                        v-else
                        class="fake-checkbox"
                        aria-hidden="true"
                      ></span>

                      <label
                        :for="tag.disabled ? null : tag.name"
                        :class="{ 'disabled-label': tag.disabled }"
                      >
                        {{ tag.value }}

                        <svg
                          v-if="!isRegimenLogic && tag.type === 'logic'"
                          xmlns="http://www.w3.org/2000/svg"
                          width="13"
                          height="13"
                          viewBox="0 0 13 13"
                          fill="none"
                        >
                          <rect width="13" height="13" rx="6.5" fill="#2C3146" />
                          <path
                            d="M7.5 4.5H8.09345V7.78087H9.5V8.5H7.5V4.5Z"
                            fill="white"
                          />
                          <path
                            d="M5.00976 6.93148H4.23415V8.5H3.5V4.5H5.26341C5.51545 4.50543 5.70894 4.5398 5.8439 4.60312C5.98049 4.66644 6.09593 4.75961 6.19024 4.88263C6.26829 4.98394 6.33008 5.09611 6.37561 5.21913C6.42114 5.34215 6.4439 5.48236 6.4439 5.63976C6.4439 5.82972 6.40081 6.01696 6.31463 6.20149C6.22846 6.38422 6.08618 6.51357 5.8878 6.58955C6.05366 6.66373 6.17073 6.76956 6.23902 6.90706C6.30894 7.04274 6.3439 7.25079 6.3439 7.53121V7.79986C6.3439 7.98259 6.35041 8.10651 6.36341 8.17164C6.38293 8.27476 6.42846 8.35075 6.5 8.39959V8.5H5.67317C5.65041 8.41135 5.63415 8.33989 5.62439 8.28562C5.60488 8.17345 5.59431 8.05857 5.59268 7.94098L5.5878 7.5692C5.58455 7.31411 5.54228 7.14405 5.46098 7.05902C5.3813 6.97399 5.23089 6.93148 5.00976 6.93148ZM5.46585 6.20421C5.61545 6.12822 5.69024 5.97806 5.69024 5.75373C5.69024 5.51131 5.61789 5.34848 5.47317 5.26526C5.39187 5.21823 5.26992 5.19471 5.10732 5.19471H4.23415V6.26934H5.08537C5.25447 6.26934 5.3813 6.24763 5.46585 6.20421Z"
                            fill="white"
                          />
                        </svg>
                      </label>
                    </div>
                  </div>
                </div>
                <div v-if="isRegimenLogic" class="col-xl-12">
                  <div class="info-msg mt-0">
                    (inactive options already have an associated Regimen)
                  </div>
                </div>
                <p
                  v-if="tagError"
                  class="validation error mt-2"
                >
                  {{ tagError }}
                </p>
              </div>

              <!-- Name -->
              <div class="row form form-gray pb-3">
                <div class="col-sm-12 form-group mb-2">
                  <label for="p_name" class="form-check-label">
                    Name: <span class="required">*</span>
                  </label>
                </div>
                <div class="col-sm-12 form-group mb-0">
                  <validation-provider
                    v-slot="{ errors }"
                    name="Name"
                    rules="required"
                  >
                    <input
                      id="p_name"
                      v-model="regimen.name"
                      type="text"
                      placeholder="Acne Prone Regimen"
                      maxlength="50"
                      :class="errors.length ? 'error' : ''"
                    >
                    <p
                      v-for="(err, errKey) in errors"
                      :key="errKey"
                      class="validation error mt-2"
                    >
                      {{ err }}
                    </p>
                  </validation-provider>
                </div>
              </div>

              <!-- Description -->
              <div class="row form form-gray pb-3">
                <div class="col-sm-12 form-group mb-2">
                  <label for="d_regimen" class="form-check-label">
                    Description: 
                  </label>
                </div>
                <div class="col-sm-12 form-group mb-0">
                  <validation-provider
                    v-slot="{ errors }"
                    name="Description"
                  >
                    <textarea
                      id="d_regimen"
                      v-model="regimen.description"
                      name="d_regimen"
                      placeholder="enter the description of the regimen"
                      maxlength="255"
                      :class="errors.length ? 'error' : ''"
                    />
                    <p
                      v-for="(err, errKey) in errors"
                      :key="errKey"
                      class="validation error mt-2"
                    >
                      {{ err }}
                    </p>
                  </validation-provider>
                </div>
              </div>

              <!-- Practice Select (Super Admin Only) -->
              <div v-if="isRole('super_admin')" class="row form form-gray pb-3">
                <div class="col-sm-12 form-group mb-2">
                  <label for="practice_select" class="form-check-label">
                    Practice: <span class="required">*</span>
                  </label>
                </div>
                <div class="col-sm-12 form-group mb-0">
                  <validation-provider
                    ref="practiceProvider"
                    v-slot="{ errors }"
                    name="Practice"
                    rules="required"
                  >
                    <div id="practice-select" class="inner-pad">
                      <SelectPractice
                        v-model="regimen.owner_id"
                        :title="currentPracticeName()"
                        :items="valuesForSelect(practices)"
                        :total="practicesTotal"
                        :loading="isPracticesLoading"
                        :name="'practice-select'"
                        :filters="filters"
                        @load-more="loadMorePractices"
                        @input="onPracticeChange"
                        @click.native="onRequiredSelectOpen('practice')"
                      />
                    </div>
                    <template v-if="showPracticeError">
                      <p
                        v-for="(err, errKey) in errors"
                        :key="errKey"
                        class="validation error mt-2"
                      >
                        {{ err }}
                      </p>
                    </template>
                  </validation-provider>
                </div>
              </div>

              <!-- Skincare Professional Dropdown -->
              <div class="row form form-gray pb-3">
                <div class="col-sm-12 form-group mb-2">
                  <label for="physician_select" class="form-check-label">
                    SkinCare Professional:
                  </label>
                </div>
                <div class="col-sm-12 form-group mb-0">
                  <div id="physician-select" class="inner-pad">
                    <Select
                      v-model="regimen.physician_id"
                      :title="currentPhysicianName()"
                      :items="physiciansForSelect()"
                      :loading="loadingPhysicians"
                      :name="'physician-select'"
                      :bottom-box="true"
                      @input="onPhysicianChange"
                      @click.native="onRequiredSelectOpen('physician')"
                    />
                  </div>
                  <template v-if="showPhysicianError">
                    <p
                      v-for="(err, errKey) in errors"
                      :key="errKey"
                      class="validation error mt-2"
                    >
                      {{ err }}
                    </p>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sticky Footer -->
        <div class="sticky-footer">
          <div class="footer-left">
            <div class="footer-info">
              <p class="footer-title">
                {{ regimen.name || 'New Regimen' }}
              </p>
              <span class="footer-subtitle">Enter Regimen Information</span>
            </div>
          </div>

          <div class="footer-actions">
            <button
              type="button"
              class="btn_custom btn_line"
              @click="$router.push({ name: 'PresetRegimenNew' })"
            >
              Back
            </button>
            <button
              type="submit"
              class="btn_custom btn_prime"
              :class="{ disabled: (invalid || tagError) }"
            >
              Next
            </button>
          </div>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>
<script>
import Header from './Header';
import {
  ValidationProvider,
  ValidationObserver,
  extend
} from 'vee-validate';
import { required, email } from 'vee-validate/dist/rules';
import { presetRegimenTags } from '@/store/regimen/presetRegimenTags';
import { PracticeService, PresetRegimenService } from '@/services/api.service';
import SelectPractice from '@/views/components/Select';
import Select from '@/views/components/Select';
import practices from '@/mixins/practices';
import { encodeQueryData } from '@/helper';
// Install rules
extend('required', required);

email['message'] = 'Please enter a valid email address';
extend('email', email);

// @vuese
// @group Preset Regimen New
export default {
  name: 'RegimenInfoNew',
  components: {
    Header,
    ValidationProvider,
    ValidationObserver,
    SelectPractice,
    Select,
  },
  mixins: [practices],
  props: {
    regimen: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      regimenTags: presetRegimenTags,
      tagError: '',
      practice: null,
      sending: false,
      isRegimenLogic: true,
      physicians: [],
      loadingPhysicians: false,
      practiceSelectInteracted: false,
      practiceSelectTouched: false,
      physicianSelectInteracted: false,
      physicianSelectTouched: false,
      usedTags: [], // Store tags that are already used
      selectedPracticeName: '',
      selectedPhysicianName: ''
    };
  },
  computed: {
    showPracticeError() {
      return this.practiceSelectTouched && !this.regimen.owner_id;
    },
    showPhysicianError() {
      return this.physicianSelectTouched && !this.regimen.physician_id;
    },
    filteredTags() {
      let tags;

      if (this.isRegimenLogic) {
        // Regimen Logic mode: only show 6 logic tags
        tags = this.regimenTags.filter(tag => tag.type === 'logic');
      } else {
        // Custom Regimen mode: show all tags
        tags = this.regimenTags;
      }

      return tags.map(tag => ({
        ...tag,
        disabled: this.isRegimenLogic && this.usedTags.includes(tag.name)
      }));
    }
  },
  watch: {
    // Watch for route changes to reload data when navigating back
    '$route.params.id': {
      handler(newId, oldId) {
        // Only reload if the ID actually changed and exists
        if (newId && newId !== oldId) {
          console.log('Route ID changed, reloading regimen:', newId);
          this.initializeComponent();
        }
      },
      immediate: false
    }
  },
  methods: {
    onRequiredSelectOpen(type) {
      if (type === 'practice') {
        this.practiceSelectInteracted = true;
      } else {
        this.physicianSelectInteracted = true;
      }
    },

    handleRequiredSelectBlur(event) {
      const practiceSelect = this.$el.querySelector('#practice-select');
      const physicianSelect = this.$el.querySelector('#physician-select');

      if (
        practiceSelect &&
        this.practiceSelectInteracted &&
        !practiceSelect.contains(event.target) &&
        !this.regimen.owner_id
      ) {
        this.practiceSelectTouched = true;
        if (this.$refs.practiceProvider) this.$refs.practiceProvider.validate();
      }

      if (
        physicianSelect &&
        this.physicianSelectInteracted &&
        !physicianSelect.contains(event.target) &&
        !this.regimen.physician_id
      ) {
        this.physicianSelectTouched = true;
        if (this.$refs.physicianProvider) this.$refs.physicianProvider.validate();
      }
    },

    async fetchUsedTags() {
      if (!this.isRegimenLogic) {
        this.usedTags = [];
        return;
      }
      
      try {
        // Get practice ID - for super_admin use selected practice, otherwise use current practice
        let practiceId = null;
        if (this.isRole('super_admin')) {
          practiceId = this.regimen.owner_id;
        } else {
          // For regular users, get practice from store or current practice
          practiceId = this.$store.state.practice.practice.id;
        }
        
        if (!practiceId) {
          console.log('No practice ID available for fetching used tags');
          this.usedTags = [];
          return;
        }
        
        const params = {};
        
        // Always include practice_id in query params
        params.practice_id = practiceId;

        // Add user_id (physician_id) if a skincare professional is selected
        if (this.regimen.physician_id) {
          params.user_id = this.regimen.physician_id;
        }
        
        // Add regimen_id if editing existing regimen (to exclude current regimen from used tags)
        if (this.regimen.id) {
          params.regimen_id = this.regimen.id;
        }
        
        console.log('Fetching used tags with params:', params);
        
        // Call API without practice in path - practice_id is in query params only
        const response = await PresetRegimenService.getUsedTags(
          '',
          encodeQueryData(params)
        );
        console.log('API response for used tags:', response);
        
        this.usedTags = response.data.used_tags || [];
        console.log('Used tags:', this.usedTags);
        
        if (this.regimen.tags && this.regimen.tags.length) {
          const invalidTags = this.regimen.tags.filter(tag =>
            this.usedTags.includes(tag)
          );

          if (invalidTags.length) {
            this.regimen.tags = this.regimen.tags.filter(
              tag => !this.usedTags.includes(tag)
            );

            if (!this.regimen.tags.length) {
              this.tagError = 'Primary Concern is required.';
            }
          }
        }
      } catch (error) {
        console.error('Error fetching used tags:', error);
        this.usedTags = [];
      }
    },
    // Handle toggle change - clear tags when switching
    handleToggleChange() {
      this.isRegimenLogic = !this.isRegimenLogic;
      this.regimen.tags = [];
      this.tagError = 'Primary Concern is required.';
      this.fetchUsedTags();
    },
    // Load regimen data for editing
    async loadRegimenData(regimenId) {
      try {
        const response = await PresetRegimenService.getPresetRegimen(regimenId);
        // Handle both possible response keys from the API
        const regimenData = response.data.regimen || response.data.preset_regimen;

        if (!regimenData) {
          console.error('loadRegimenData: no regimen data in response', response.data);
          return;
        }
        
        // Populate regimen object (but NOT physician_id yet - wait for physicians to load)
        this.regimen.id = regimenData.id;
        this.regimen.name = regimenData.name;
        this.regimen.description = regimenData.description;
        this.regimen.tags = (regimenData.tags || []).map(tag => 
          typeof tag === 'string' ? tag : tag.name
        );
        this.regimen.owner_id = regimenData.practice_id || regimenData.owner_id;
        this.regimen.practice = regimenData.practice || this.regimen.practice;
        this.selectedPracticeName = regimenData.practice && regimenData.practice.name ? regimenData.practice.name : this.selectedPracticeName;
        this.isRegimenLogic = regimenData.regimen_type === 'custom' ? false : true;
        
        // Handle physician/user ID - API might return user object or just ID
        let physicianId = null;
        if (regimenData.user && typeof regimenData.user === 'object') {
          // API returns user object
          physicianId = regimenData.user.id;
          this.selectedPhysicianName = [regimenData.user.first_name, regimenData.user.last_name].filter(Boolean).join(' ');
        } else if (regimenData.physician && typeof regimenData.physician === 'object') {
          // API returns physician object
          physicianId = regimenData.physician.id;
          this.selectedPhysicianName = [regimenData.physician.first_name, regimenData.physician.last_name].filter(Boolean).join(' ');
        } else {
          // API returns just the ID
          physicianId = regimenData.physician_id || regimenData.user_id;
        }

        // Store regimen ID in localStorage for navigation persistence
        localStorage.setItem('currentRegimenId', regimenId);
        
        // Load physicians for the relevant practice FIRST so the dropdown is populated
        if (this.isRole('super_admin') && this.regimen.owner_id) {
          await this.fetchPhysicians(this.regimen.owner_id);
        } else if (!this.isRole('super_admin')) {
          await this.fetchPhysicians();
        }
        
        // NOW set the physician_id after physicians are loaded
        this.regimen.physician_id = physicianId;
        this.regimen.user_id = physicianId;
        
        // Force Vue to update the computed properties
        this.$nextTick(() => {
          this.$forceUpdate();
        });

        // Fetch used tags for the loaded regimen
        this.fetchUsedTags();
      } catch (error) {
        console.error('Error loading regimen data:', error);
        this.$toasted.error('Failed to load regimen data');
      }
    },
    // @vuese
    // Used to validate if all needed data entered and then redirect to other page
    async enterInfo() {
      if (this.sending) return;

      if (this.regimen.tags.length) {
        this.tagError = '';
      } else {
        this.tagError = 'Primary Concern is required.';
      }

      if (await this.isValid()) {
        this.sending = true;

        // Prepare payload with tags as array of objects
        const payload = {
          regimen: {
            name: this.regimen.name,
            description: this.regimen.description,
            tags: this.regimen.tags.map(tag => ({ name: tag })),
            regimen_type: this.isRegimenLogic ? 'rl_regimen' : 'custom',
            user_id: this.regimen.physician_id
          }
        };

        // Add owner_id if super_admin and practice selected
        if (this.isRole('super_admin') && this.regimen.owner_id) {
          payload.regimen.owner_id = this.regimen.owner_id;
        }

        // Use update if regimen has an ID, otherwise create
        const apiCall = this.regimen.id
          ? PresetRegimenService.update(payload, this.regimen.id, this.getRequestPracticeId())
          : PresetRegimenService.create(payload, this.getRequestPracticeId());

        apiCall
          .then(resp => {
            
            // Get the regimen data from response - try both possible keys
            let savedRegimen = null;
            
            if (resp.data.preset_regimen) {
              savedRegimen = resp.data.preset_regimen;
            } else if (resp.data.regimen) {
              savedRegimen = resp.data.regimen;
            } else {
              console.error('No regimen data found in response. Available keys:', Object.keys(resp.data));
            }
            
            if (!savedRegimen || !savedRegimen.id) {
              this.flashWarning('Regimen saved but missing ID. Please refresh the page.');
              return;
            }

            const wasCreate = !this.regimen.id;
            
            // Update local regimen with response data including ID
            Object.assign(this.regimen, savedRegimen);
            
            // Navigate to edit route with the ID from response
            this.$router.push({ 
              name: 'RegimenEditPresetNew', 
              params: { id: savedRegimen.id.toString() } 
            }).then(() => {
              if (wasCreate) {
                sessionStorage.setItem('presetRegimenCreated', savedRegimen.id.toString());
              }
              console.log('Navigation successful');
            }).catch(err => {
              console.error('Navigation error:', err);
            });
          })
          .catch(err => {
            console.error('Error saving preset regimen:', err);

            const errors = err.response?.data?.errors;

            if (errors?.tags) {
              const tagErrors = Array.isArray(errors.tags)
                ? errors.tags
                : [errors.tags];

              this.tagError = tagErrors.join(', ');
              return;
            }

            this.flashWarning('Failed to save preset regimen. Please try again.');
          })
          .finally(() => {
            this.sending = false;
          });
      }
    },
    // @vuese
    // Used to validate all data
    async isValid() {
      return await this.$refs.observer.validate() && this.tagError == '';
    },
    // @vuese
    // Used to add and remove tags from regimen
    // @arg Tag key
    handleTag(tagValue) {
      // Allow multiple selections for both regimen types
      const tagIndex = this.regimen.tags.findIndex(tag => {
        return tag == tagValue;
      });
      if (tagIndex != -1) {
        this.regimen.tags.splice(tagIndex, 1);
      } else {
        this.regimen.tags.push(tagValue);
        this.tagError = '';
      }

      if(!this.regimen.tags.length){
        this.tagError = 'Primary Concern is required.';
      }
    },
    // @vuese
    // Used to check if tag alreadt added to regimen
    // @arg Tag key
    tagChacked(tagValue) {
      return this.regimen.tags.find(tag => tag == tagValue);
    },

    currentPracticeName() {
      if (!this.regimen || !this.regimen.owner_id) {
        return 'Select practice';
      }

      if (this.regimen.practice && this.regimen.practice.name) {
        return this.regimen.practice.name;
      }

      const selectedPractice = this.practices.find(practice => practice.id === this.regimen.owner_id);
      if (selectedPractice && selectedPractice.name) {
        return selectedPractice.name;
      }

      return this.selectedPracticeName || 'Select practice';
    },

    currentPhysicianName() {
      if (this.regimen.physician_id) {
        const physician = this.physicians.find(p => String(p.id) === String(this.regimen.physician_id));
        console.log('physicianData', physician.first_name);
        return physician ? physician.first_name : (this.selectedPhysicianName || 'Select Physician');
      }
      return 'Select Professional';
    },

    physiciansForSelect() {
      return this.physicians.map(physician => ({
        title: physician.name,
        value: String(physician.id)
      }));
    },
    
    getRequestPracticeId() {
      if (this.isRole('super_admin')) {
        return this.regimen.owner_id ? `/${this.regimen.owner_id}` : '';
      }
      return '';
    },

    onPracticeChange(practiceId) {
      // When practice changes, reset physician and load physicians for new practice
      this.regimen.physician_id = '';
      const selectedPractice = this.practices.find(practice => practice.id === practiceId);
      this.selectedPracticeName = selectedPractice && selectedPractice.name ? selectedPractice.name : '';
      this.selectedPhysicianName = '';
      this.practiceSelectTouched = this.practiceSelectInteracted && !practiceId;
      this.physicianSelectTouched = false;
      this.physicianSelectInteracted = false;
      if (this.practiceSelectTouched && this.$refs.practiceProvider) this.$refs.practiceProvider.validate();
      this.fetchUsedTags();
      this.fetchPhysicians(practiceId);
    },

    async fetchPhysicians(practiceId = null) {
      this.loadingPhysicians = true;
      try {
        const pid = practiceId;
        const response = await PracticeService.getPhysicians(pid);
        // Handle nested response structure: physicians.items
        const physiciansData = response.data.physicians;
        this.physicians = Array.isArray(physiciansData) ? physiciansData : ((physiciansData && physiciansData.items) || []);
        if (this.regimen.physician_id) {
          const selectedPhysician = this.physicians.find(physician => String(physician.id) === String(this.regimen.physician_id));
          if (selectedPhysician && selectedPhysician.name) {
            this.selectedPhysicianName = selectedPhysician.name;
          }
        }
      } catch (err) {
        console.error('Error fetching physicians:', err);
        this.physicians = [];
      } finally {
        this.loadingPhysicians = false;
      }
    },

    onPhysicianChange(physicianId) {
      // When physician changes, fetch used tags for that physician
      this.regimen.physician_id = physicianId ? String(physicianId) : '';
      const selectedPhysician = this.physicians.find(physician => String(physician.id) === String(physicianId));
      this.selectedPhysicianName = selectedPhysician && selectedPhysician.name ? selectedPhysician.name : '';
      this.physicianSelectTouched = this.physicianSelectInteracted && !physicianId;
      if (this.physicianSelectTouched && this.$refs.physicianProvider) this.$refs.physicianProvider.validate();
      this.fetchUsedTags();
    },

    // Initialize or reinitialize the component with regimen data
    async initializeComponent() {
      const regimenId = this.$route.params.id;
      const userId = this.$route.params.user_id;
      
      // If user_id is passed via route params, set it immediately
      if (userId) {
        this.regimen.physician_id = String(userId);
        this.regimen.user_id = String(userId);
      }
      
      if (regimenId) {
        // Fetch regimen data from API
        await this.loadRegimenData(regimenId);
      }
      
      // Fetch physicians on mount only when NOT loading from API
      // (loadRegimenData already calls fetchPhysicians when editing)
      if (!this.$route.params.id) {
        if (this.isRole('super_admin') && this.regimen.owner_id) {
          this.fetchPhysicians(this.regimen.owner_id);
        } else if (!this.isRole('super_admin')) {
          this.fetchPhysicians();
        }
      }
    }
  },
  async mounted() {
    document.addEventListener('click', this.handleRequiredSelectBlur);
    // Initialize the component
    await this.initializeComponent();
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleRequiredSelectBlur);
  }
};
</script>

<style scoped>
  .disabled-label {
    opacity: 0.5;
    color: #999;
    cursor: not-allowed;
  }
  .fake-checkbox {
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 5px;
    vertical-align: middle;
    border: 1px solid #ccc;
    border-radius: 2px;
    background: #f5f5f5;
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
